import { useEffect, useState, useRef } from 'react';
import { t } from '../i18n';

const API = '/api/freelance';
function authHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
}
const C = { bg: '#FCFCFA', surface: '#fff', border: '#E3E1D9', text: '#2C2C2A', muted: '#5F5E5A', primary: '#0F6E56', accent: '#4F46E5' };
const inp = { width: '100%', padding: '8px 10px', fontSize: 13, border: `0.5px solid ${C.border}`, borderRadius: 8, boxSizing: 'border-box', marginBottom: 8 };
const lbl = { fontSize: 12, color: C.muted, display: 'block', marginBottom: 3 };
const btn = { padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: C.primary, color: '#fff', cursor: 'pointer' };
const btnGhost = { ...btn, background: '#fff', color: C.text, border: `0.5px solid ${C.border}` };
const TEMPLATES = { classic: 'Classic', modern: 'Modern', compact: 'Compact' };

const emptyExp = () => ({ title: '', org: '', start: '', end: '', detail: '' });
const emptyEdu = () => ({ title: '', org: '', start: '', end: '', detail: '' });
const emptyCert = () => ({ name: '', issuer: '', year: '' });

export default function CVBuilder() {
  const [cv, setCv] = useState(null);
  const [msg, setMsg] = useState('');
  const printRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/cv/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => {
      if (d.exists) setCv(d);
      else setCv({ ...d.prefill });
    }).catch(() => setCv({}));
  }, []);

  if (!localStorage.getItem('token')) return <p style={{ padding: '2rem', fontSize: 14 }}>{t('cv_please_login')}</p>;
  if (!cv) return <p style={{ padding: '2rem', fontSize: 13, color: C.muted }}>{t('cv_loading')}</p>;

  const set = (k) => (e) => setCv({ ...cv, [k]: e.target.value });
  const list = (key) => cv[key] || [];
  const addItem = (key, factory) => setCv({ ...cv, [key]: [...list(key), factory()] });
  const updItem = (key, i, field) => (e) => { const arr = list(key).slice(); arr[i] = { ...arr[i], [field]: e.target.value }; setCv({ ...cv, [key]: arr }); };
  const delItem = (key, i) => { const arr = list(key).slice(); arr.splice(i, 1); setCv({ ...cv, [key]: arr }); };

  const save = async () => {
    const res = await fetch(`${API}/cv/save/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(cv) });
    const d = await res.json();
    setMsg(d.success ? '✅ ' + t('cv_saved') : (d.error || t('cv_save_failed')));
  };

  const downloadPDF = () => {
    const html = printRef.current.innerHTML;
    const w = window.open('', '_blank');
    if (!w) { setMsg(t('cv_popup_blocked')); return; }
    w.document.write(`<html><head><title>${cv.full_name || 'CV'} — Resume</title>
      <style>body{font-family:Georgia,serif;color:#222;margin:0;padding:32px;} *{box-sizing:border-box;}</style>
      </head><body>${html}</body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 300);
  };

  const skillList = (cv.skills || '').split(',').map((s) => s.trim()).filter(Boolean);
  const tmpl = cv.template || 'classic';
  const headColor = tmpl === 'modern' ? C.accent : C.primary;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: C.bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 22, margin: 0 }}>📄 CV Builder</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btnGhost} onClick={save}>💾 {t('cv_save')}</button>
          <button style={btn} onClick={downloadPDF}>⬇ {t('cv_download')}</button>
        </div>
      </div>
      {msg && <p style={{ fontSize: 13, color: C.primary }}>{msg}</p>}
      <p style={{ fontSize: 12, color: C.muted, margin: '4px 0 16px' }}>{t('cv_autofill_note')}</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
        {/* EDITOR */}
        <div>
          <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '1rem', marginBottom: 12 }}>
            <label style={lbl}>{t('cv_template')}</label>
            <select style={inp} value={tmpl} onChange={set('template')}>
              {Object.entries(TEMPLATES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <label style={lbl}>{t('cv_full_name')}</label><input style={inp} value={cv.full_name || ''} onChange={set('full_name')} />
            <label style={lbl}>{t('cv_headline')}</label><input style={inp} value={cv.headline || ''} onChange={set('headline')} />
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><label style={lbl}>Email</label><input style={inp} value={cv.email || ''} onChange={set('email')} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>Phone</label><input style={inp} value={cv.phone || ''} onChange={set('phone')} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><label style={lbl}>City</label><input style={inp} value={cv.city || ''} onChange={set('city')} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>Country</label><input style={inp} value={cv.country || ''} onChange={set('country')} /></div>
            </div>
            <label style={lbl}>Website / LinkedIn</label><input style={inp} value={cv.website || ''} onChange={set('website')} />
            <label style={lbl}>Professional summary</label>
            <textarea style={{ ...inp, minHeight: 70 }} value={cv.summary || ''} onChange={set('summary')} />
            <label style={lbl}>{t('cv_skills')}</label><input style={inp} value={cv.skills || ''} onChange={set('skills')} placeholder="React, Django, Pi SDK" />
            <label style={lbl}>{t('cv_languages')}</label><input style={inp} value={cv.languages || ''} onChange={set('languages')} placeholder="Urdu, English" />
          </div>

          <SectionEditor title={t("cv_experience")} items={list('experience')} onAdd={() => addItem('experience', emptyExp)}
            renderItem={(it, i) => (
              <>
                <input style={inp} value={it.title} onChange={updItem('experience', i, 'title')} placeholder="Job title" />
                <input style={inp} value={it.org} onChange={updItem('experience', i, 'org')} placeholder="Company" />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={inp} value={it.start} onChange={updItem('experience', i, 'start')} placeholder="2022" />
                  <input style={inp} value={it.end} onChange={updItem('experience', i, 'end')} placeholder="Present" />
                </div>
                <textarea style={{ ...inp, minHeight: 50 }} value={it.detail} onChange={updItem('experience', i, 'detail')} placeholder="What you did" />
              </>
            )} onDel={(i) => delItem('experience', i)} />

          <SectionEditor title={t("cv_education")} items={list('education')} onAdd={() => addItem('education', emptyEdu)}
            renderItem={(it, i) => (
              <>
                <input style={inp} value={it.title} onChange={updItem('education', i, 'title')} placeholder="Degree" />
                <input style={inp} value={it.org} onChange={updItem('education', i, 'org')} placeholder="Institution" />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={inp} value={it.start} onChange={updItem('education', i, 'start')} placeholder="2018" />
                  <input style={inp} value={it.end} onChange={updItem('education', i, 'end')} placeholder="2022" />
                </div>
              </>
            )} onDel={(i) => delItem('education', i)} />

          <SectionEditor title={t("cv_certifications")} items={list('certifications')} onAdd={() => addItem('certifications', emptyCert)}
            renderItem={(it, i) => (
              <>
                <input style={inp} value={it.name} onChange={updItem('certifications', i, 'name')} placeholder="Certification" />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={inp} value={it.issuer} onChange={updItem('certifications', i, 'issuer')} placeholder="Issuer" />
                  <input style={inp} value={it.year} onChange={updItem('certifications', i, 'year')} placeholder="Year" />
                </div>
              </>
            )} onDel={(i) => delItem('certifications', i)} />
        </div>

        {/* PREVIEW */}
        <div>
          <p style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Live preview</p>
          <div ref={printRef} style={{ background: '#fff', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: 28, fontFamily: 'Georgia, serif', color: '#222', minHeight: 400 }}>
            <div style={{ borderBottom: `3px solid ${headColor}`, paddingBottom: 10, marginBottom: 14 }}>
              <h2 style={{ margin: 0, fontSize: 24, color: headColor }}>{cv.full_name || t('cv_your_name')}</h2>
              {cv.headline && <p style={{ margin: '2px 0 0', fontSize: 14, color: '#555' }}>{cv.headline}</p>}
              <p style={{ margin: '6px 0 0', fontSize: 11, color: '#666' }}>
                {[cv.email, cv.phone, [cv.city, cv.country].filter(Boolean).join(', '), cv.website].filter(Boolean).join('  •  ')}
              </p>
            </div>
            {cv.summary && <Section head={t("cv_summary")} color={headColor}><p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5 }}>{cv.summary}</p></Section>}
            {skillList.length > 0 && (
              <Section head={t("cv_skills")} color={headColor}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {skillList.map((s, i) => <span key={i} style={{ fontSize: 11, background: '#f0efe9', borderRadius: 4, padding: '2px 8px' }}>{s}</span>)}
                </div>
              </Section>
            )}
            {list('experience').length > 0 && (
              <Section head={t("cv_experience")} color={headColor}>
                {list('experience').map((e, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{e.title}{e.org && ` — ${e.org}`}</p>
                    <p style={{ margin: 0, fontSize: 11, color: '#777' }}>{[e.start, e.end].filter(Boolean).join(' – ')}</p>
                    {e.detail && <p style={{ margin: '2px 0 0', fontSize: 12, lineHeight: 1.4 }}>{e.detail}</p>}
                  </div>
                ))}
              </Section>
            )}
            {list('education').length > 0 && (
              <Section head={t("cv_education")} color={headColor}>
                {list('education').map((e, i) => (
                  <div key={i} style={{ marginBottom: 6 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{e.title}{e.org && ` — ${e.org}`}</p>
                    <p style={{ margin: 0, fontSize: 11, color: '#777' }}>{[e.start, e.end].filter(Boolean).join(' – ')}</p>
                  </div>
                ))}
              </Section>
            )}
            {list('certifications').length > 0 && (
              <Section head={t("cv_certifications")} color={headColor}>
                {list('certifications').map((c, i) => (
                  <p key={i} style={{ margin: '0 0 3px', fontSize: 12 }}>{c.name}{c.issuer && `, ${c.issuer}`}{c.year && ` (${c.year})`}</p>
                ))}
              </Section>
            )}
            {cv.languages && <Section head={t("cv_languages")} color={headColor}><p style={{ margin: 0, fontSize: 12 }}>{cv.languages}</p></Section>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ head, color, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color }}>{head}</p>
      {children}
    </div>
  );
}

function SectionEditor({ title, items, onAdd, renderItem, onDel }) {
  return (
    <div style={{ background: '#fff', border: '0.5px solid #E3E1D9', borderRadius: 12, padding: '1rem', marginBottom: 12 }}>
      <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>{title}</p>
      {items.map((it, i) => (
        <div key={i} style={{ border: '0.5px dashed #E3E1D9', borderRadius: 8, padding: 8, marginBottom: 8 }}>
          {renderItem(it, i)}
          <button onClick={() => onDel(i)} style={{ fontSize: 11, color: '#A32D2D', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>✕ {t('cv_remove')}</button>
        </div>
      ))}
      <button onClick={onAdd} style={{ fontSize: 12, padding: '6px 12px', border: '0.5px solid #E3E1D9', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>➕ {t('cv_add')} {title.toLowerCase()}</button>
    </div>
  );
}
